import React, { useEffect, useState } from 'react';
import { getShiftRosterGroupDetail, updateShiftRosterCell } from '../../../services/shiftRoasterService';
import { getUsers } from '../../../services/userService';
import Select, { MultiValue } from 'react-select';
import { formatDate } from '../../../utils/date';

interface Personnel {
  id: number;
  name: string;
  role: string;
  avatar: string;
}

interface EditPersonnelModalProps {
  onClose: () => void;
  onSuccess?: () => void;
  groupId: number;
  shiftRosterId: number;
  rosterDate: string;
}

interface UserOption {
  value: number;
  label: string;
}

const EditPersonnelModal: React.FC<EditPersonnelModalProps> = ({ onClose, onSuccess, groupId, shiftRosterId, rosterDate }) => {
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [allUsers, setAllUsers] = useState<Personnel[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<readonly UserOption[]>([]);
  const [groupData, setGroupData] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const fetchPersonnel = async () => {
    try {
      setLoading(true);

      const response = await getShiftRosterGroupDetail(groupId);

      const apiData = response.data?.data || response;

      setGroupData(apiData);

      const users = apiData.users || apiData.personnels || apiData.members || [];

      const formatted = users.map((user: any) => ({
        id: user.user_id,
        name: user.user.name,
        role: user.designation || user.role || '',
        avatar: user.profile_image || user.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}`,
      }));

      setPersonnel(formatted);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await getUsers(1);

      const users = response.data?.data || response.data || response.users || [];

      const formatted = users.map((user: any) => ({
        id: user.id,
        name: user.name,
        role: user.designation?.name || user.role || '',
        avatar: user.profile_image || user.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}`,
      }));

      setAllUsers(formatted);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchPersonnel();
    fetchAllUsers();
  }, [groupId]);

  const removePersonnel = (id: number) => {
    setPersonnel(personnel.filter((p) => p.id !== id));
  };

  const handleNext = () => setStep(2);
  const handleBack = () => setStep(1);
  const handleConfirm = async () => {
    try {
      setSaving(true);

      const payload = {
        shift_roster_id: shiftRosterId,
        group_id: groupId,
        roster_date: rosterDate,
        user_ids: personnel.map((person) => person.id),
      };

      // Personnel selected from a calendar cell are a date-specific override.
      // Never update /shift-roster-groups here because that is the global master pool.
      await updateShiftRosterCell(payload);
      await onSuccess?.();

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
      }, 2000);
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const userOptions: UserOption[] = allUsers.map((user) => ({
    value: user.id,
    label: user.name,
  }));

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[600px] overflow-y-auto">
        {/* Header */}
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="font-semibold text-xl">Edit Personnel</div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl leading-none">
            ×
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="border-b py-5">
          <div className="w-[280px] mx-auto">
            <div className="flex items-start">
              <div className="flex-1">
                <div className="flex items-center">
                  <div className={`w-2.5 h-2.5 rounded-full ${step >= 1 ? 'bg-green-500' : 'bg-gray-300'}`} />

                  <div className={`flex-1 h-[1px] ${step > 1 ? 'bg-green-500' : 'bg-gray-300'}`} />
                </div>

                <p className={`mt-3 text-xs font-medium ${step === 1 ? 'text-green-600' : 'text-gray-400'}`}>Personnel Management</p>
              </div>

              <div>
                <div className={`w-2.5 h-2.5 rounded-full ${step >= 2 ? 'bg-green-500' : 'bg-gray-300'}`} />

                <p className={`mt-3 text-xs font-medium whitespace-nowrap ${step === 2 ? 'text-green-600' : 'text-gray-400'}`}>Preview</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex">
          {/* Step 1: Personnel Management */}
          <div className={`flex-1 p-6 ${step === 1 ? 'block' : 'hidden'}`}>
            <div className="mb-6">
              <h3 className="font-semibold mb-4">Personnel Management</h3>

              <div className="flex gap-2 mb-4">
                <Select
                  isMulti
                  options={userOptions}
                  value={selectedUsers}
                  onChange={(value: MultiValue<UserOption>) => {
                    setSelectedUsers(value);
                  }}
                  placeholder="Select Personnel"
                  className="flex-1"
                  closeMenuOnSelect={false}
                />
                <button
                  onClick={() => {
                    const selectedIds = selectedUsers.map((item) => item.value);

                    const usersToAdd = allUsers.filter((user) => selectedIds.includes(user.id) && !personnel.some((p) => p.id === user.id));
                    setPersonnel((prev) => [...prev, ...usersToAdd]);

                    setSelectedUsers([]);
                  }}
                  className="px-5 py-2 border border-gray-300 rounded-md"
                >
                  Add
                </button>
              </div>

              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-600 w-16">Sl. No.</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Shift Attendants &amp; Assistants</th>
                      <th className="w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={3} className="text-center py-8">
                          Loading...
                        </td>
                      </tr>
                    ) : (
                      personnel.map((person, idx) => (
                        <tr key={person.id} className="border-b last:border-none hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-600 font-medium">{idx + 1}</td>

                          <td className="px-4 py-3">
                            <div className="flex items-center gap-4">
                              <img src={person.avatar} alt={person.name} className="w-9 h-9 rounded-full ring-1 ring-gray-200" />

                              <div>
                                <div className="font-medium">{person.name}</div>
                                <div className="text-sm text-gray-500">{person.role}</div>
                              </div>
                            </div>
                          </td>

                          <td className="px-4 py-3">
                            <button onClick={() => removePersonnel(person.id)} className="text-gray-400 hover:text-red-600">
                              🗑
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-5">
              <button onClick={handleNext} className="px-10 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg">
                Next
              </button>
            </div>
          </div>

          {/* Step 2: Preview */}
          <div className={`flex-1 p-6 ${step === 2 ? 'block' : 'hidden'}`}>
            <div className="mb-8">
              <h3 className="font-semibold mb-5">Group Preview</h3>

              <div className="grid grid-cols-3 gap-8 mb-8 text-sm">
                <div className="font-medium">{formatDate(groupData?.date)}</div>

                <div className="font-medium">{groupData?.group_name}</div>

                <div className="font-medium">{groupData?.department?.name}</div>
              </div>

              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-600 w-16">Sl. No.</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Shift Attendants &amp; Assistants</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={2} className="text-center py-8">
                          Loading...
                        </td>
                      </tr>
                    ) : (
                      personnel.map((person, idx) => (
                        <tr key={person.id} className="border-b last:border-none">
                          <td className="px-6 py-5">{idx + 1}</td>

                          <td className="px-6 py-5">
                            <div className="flex items-center gap-4">
                              <img src={person.avatar} alt={person.name} className="w-7 h-7 rounded-full" />

                              <div>
                                <div className="font-medium">{person.name}</div>
                                <div className="text-sm text-gray-500">{person.role}</div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-5">
              <button onClick={handleBack} className="px-8 py-3 text-gray-600 font-medium hover:bg-gray-100 rounded-lg">
                Back
              </button>
              <button onClick={handleConfirm} disabled={saving} className="px-10 py-3 bg-red-600 text-white rounded-lg">
                {saving ? 'Saving...' : 'Confirm Changes'}
              </button>
            </div>

            {showSuccess && (
              <div className="fixed bottom-6 right-6 z-[9999]">
                <div className="bg-green-500 text-white rounded-lg shadow-2xl w-[330px] p-5 relative animate-slideIn">
                  {/* Close Button */}
                  <button onClick={() => setShowSuccess(false)} className="absolute top-3 right-3 text-white text-lg">
                    ×
                  </button>

                  <div className="flex items-start gap-3">
                    {/* Tick Icon */}
                    <div className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center">✓</div>

                    <div>
                      <h3 className="font-semibold text-lg">Personnel Updated</h3>

                      <p className="text-sm mt-1 text-green-100">Personnel updated successfully.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPersonnelModal;
